import { useCallback, useEffect, useRef, useState } from 'react';

// Distance from a scroll edge at which the next page is fetched
const SCROLL_THRESHOLD_PX = 100;
const SCROLL_DEBOUNCE_MS = 80;
// Breathing room kept above a jump target so it doesn't sit flush under the toolbar
const DEFAULT_SCROLL_GAP_PX = 24;
// Tolerance when matching a navigator entry to a rendered turn by timestamp
const TIMESTAMP_MATCH_S = 5;

/** Minimal message shape the hook needs; any richer type is assignable */
export interface ChatWindowMessage {
  id: string;
  role: string;
  timestamp: Date | string | number;
}

/** The page's data source. Each chat surface supplies its own implementation
 * backed by its message store. */
export interface ChatWindowAdapter {
  /** Replace the loaded list with a window starting at the target (inclusive).
   * Return whether the target is in the new window, or null to skip the jump
   * (store not ready, read-only view, another load in flight, ...). */
  jumpToMessage: (id: string) => Promise<{ found: boolean } | null>;
  /** Directional pagination. Implementations must self-guard (has-more,
   * already-loading, current view). */
  loadOlder: (container: HTMLDivElement) => void;
  loadNewer: () => void;
}

export interface UseChatWindowOptions {
  containerRef: React.RefObject<HTMLDivElement>;
  /** Rendered messages, oldest first */
  messages: ChatWindowMessage[];
  /** Resolve a rendered message's element — the page's DOM contract */
  getMessageElement: (id: string) => HTMLElement | null;
  adapter: ChatWindowAdapter;
  /** Called when a jump target can't be loaded (stale navigator index) */
  onStaleTarget?: () => void;
  scrollGapPx?: number;
}

const toEpochS = (t: Date | string | number) => new Date(t as any).getTime() / 1000;

// When the navigator index and the rendered list disagree on a message's id
// (e.g. an optimistic send still holds its temp id), find the rendered user
// turn closest to the index entry's timestamp.
const findUserMessageNearTimestamp = (
  messages: ChatWindowMessage[],
  timestamp: number,
): ChatWindowMessage | undefined => {
  let best: ChatWindowMessage | undefined;
  let bestDelta = Infinity;
  for (const m of messages) {
    if (m.role !== 'user') continue;
    const delta = Math.abs(toEpochS(m.timestamp) - timestamp);
    if (delta < bestDelta) {
      bestDelta = delta;
      best = m;
    }
  }
  return bestDelta < TIMESTAMP_MATCH_S ? best : undefined;
};

/**
 * Headless wiring between a ChatNavigator rail and a paginated message list:
 * jump-to-turn orchestration (scroll if rendered, else fetch a window via the
 * adapter and scroll once it commits) and edge-of-scroll pagination triggers
 * that fire only on user-driven scrolling — a jump's own programmatic scroll
 * must never load pages, or the viewport shifts right after landing.
 *
 * Wire the returned handlers onto the scroll container (compose onScroll with
 * any page-specific scroll logic) and pass jumpTo as ChatNavigator's onSelect.
 */
export function useChatWindow({
  containerRef,
  messages,
  getMessageElement,
  adapter,
  onStaleTarget,
  scrollGapPx = DEFAULT_SCROLL_GAP_PX,
}: UseChatWindowOptions) {
  // Message id waiting to be scrolled to once its window render commits
  const pendingScrollIdRef = useRef<string | null>(null);
  // The nonce forces the pending-scroll effect even when a window replacement
  // leaves the messages array length unchanged
  const [scrollNonce, setScrollNonce] = useState(0);
  // Cleared by programmatic jumps, re-armed by any real scroll input
  const userScrollIntentRef = useRef(true);
  const debounceTimerRef = useRef<number | undefined>();
  const jumpInFlightRef = useRef(false);

  // Instant, not smooth: a smooth animation would be cancelled by the first
  // programmatic scrollTop write (e.g. pagination's scroll compensation)
  const scrollMessageIntoView = useCallback((id: string): boolean => {
    const el = getMessageElement(id);
    const container = containerRef.current;
    if (!el || !container) return false;
    const elTop =
      el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
    container.scrollTo({ top: Math.max(0, elTop - scrollGapPx), behavior: 'auto' });
    // This scroll is not the user's — don't let it trigger pagination
    userScrollIntentRef.current = false;
    return true;
  }, [containerRef, getMessageElement, scrollGapPx]);

  const jumpTo = useCallback(async (id: string, timestamp?: number) => {
    // A new selection supersedes any unresolved previous jump
    pendingScrollIdRef.current = null;

    // Fast path: already rendered
    if (scrollMessageIntoView(id)) return;

    // Target's timestamp inside the loaded range means the element is missing
    // due to an id mismatch, not missing messages — resolve it locally
    const oldest = messages[0];
    const newest = messages[messages.length - 1];
    if (timestamp !== undefined && oldest && newest) {
      const oldestTs = toEpochS(oldest.timestamp);
      const newestTs = toEpochS(newest.timestamp);
      if (timestamp >= oldestTs - 1 && timestamp <= newestTs + 1) {
        const match = findUserMessageNearTimestamp(messages, timestamp);
        if (match) scrollMessageIntoView(match.id);
        return;
      }
    }

    // Windowed jump: the adapter replaces the list with a window at the target
    if (jumpInFlightRef.current) return;
    jumpInFlightRef.current = true;
    let result: { found: boolean } | null = null;
    try {
      result = await adapter.jumpToMessage(id);
    } finally {
      jumpInFlightRef.current = false;
    }
    if (!result) return;
    if (!result.found) {
      onStaleTarget?.();
      return;
    }
    // Defer the scroll to the effect below so it runs after React commits
    pendingScrollIdRef.current = id;
    setScrollNonce(n => n + 1);
  }, [messages, scrollMessageIntoView, adapter, onStaleTarget]);

  // Perform the pending jump scroll once the target message is rendered
  useEffect(() => {
    const id = pendingScrollIdRef.current;
    if (!id) return;
    if (scrollMessageIntoView(id)) {
      pendingScrollIdRef.current = null;
    }
  }, [scrollNonce, messages, scrollMessageIntoView]);

  const onScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    window.clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = window.setTimeout(() => {
      // Programmatic scrolls (navigator jumps) must not load pages
      if (!userScrollIntentRef.current) return;
      if (scrollTop < SCROLL_THRESHOLD_PX) {
        adapter.loadOlder(container);
      } else if (distanceFromBottom < SCROLL_THRESHOLD_PX) {
        adapter.loadNewer();
      }
    }, SCROLL_DEBOUNCE_MS);
  }, [adapter]);

  // At a scroll boundary further scrolling emits no scroll events — e.g. a
  // jump lands with its target as the window's first message, parked at
  // scrollTop 0. The wheel is the only signal there, so it re-arms intent and
  // triggers the edge loads itself, keyed on the direction the user is going.
  const onWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    userScrollIntentRef.current = true;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (e.deltaY < 0 && scrollTop < SCROLL_THRESHOLD_PX) {
      adapter.loadOlder(e.currentTarget);
    } else if (e.deltaY > 0 && scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD_PX) {
      adapter.loadNewer();
    }
  }, [adapter]);

  // Touch and scrollbar scrolling re-arm intent; their scroll events then
  // drive the zone triggers in onScroll
  const armUserScroll = useCallback(() => {
    userScrollIntentRef.current = true;
  }, []);

  useEffect(() => () => window.clearTimeout(debounceTimerRef.current), []);

  return { jumpTo, scrollMessageIntoView, onScroll, onWheel, armUserScroll };
}
